import React, { useState, useEffect, useRef } from 'react';
import { FiBell, FiX, FiAlertTriangle, FiInfo, FiCheckCircle, FiXCircle, FiArrowRight } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const TYPE_STYLES = {
  warning: { icon: FiAlertTriangle, color: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/20' },
  info: { icon: FiInfo, color: 'text-brand-400', bg: 'bg-brand-500/10', border: 'border-brand-500/20' },
  success: { icon: FiCheckCircle, color: 'text-accent-400', bg: 'bg-accent-500/10', border: 'border-accent-500/20' },
  error: { icon: FiXCircle, color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/20' },
};

const AlertBell = ({ workspaceId }) => {
  const [alerts, setAlerts] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);
  const navigate = useNavigate();

  const unreadCount = alerts.filter(a => !a.read).length;

  useEffect(() => {
    if (!workspaceId) return;
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 60000); // Poll every minute
    return () => clearInterval(interval);
  }, [workspaceId]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchAlerts = async () => {
    try {
      const res = await api.get(`/getAlertsFunction?workspaceId=${workspaceId}&includeRead=true`);
      if (res.data.success) {
        setAlerts(res.data.data || []);
      }
    } catch {
      // Silently fail — alerts are non-critical
    }
  };

  const handleDismiss = async (alertId, e) => {
    e.stopPropagation();
    setAlerts(prev => prev.filter(a => a.id !== alertId));
    try {
      await api.post('/dismissAlertFunction', { workspaceId, alertId });
    } catch {
      // Alert already removed from UI
    }
  };

  const handleAlertClick = (alert) => {
    if (alert.actionUrl) {
      navigate(alert.actionUrl);
      setIsOpen(false);
    }
  };

  return (
    <div className="relative" ref={panelRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-400 hover:text-brand-400 transition-colors rounded-lg hover:bg-white/5"
        title="Notifications"
      >
        <FiBell size={20} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4.5 h-4.5 bg-gradient-to-r from-brand-500 to-accent-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse shadow-lg shadow-brand-500/30">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto neon-card border-surface-700 shadow-2xl shadow-black/40 z-50">
          <div className="px-4 py-3 border-b border-white/5">
            <h3 className="text-sm font-semibold text-gray-800 dark:text-white">Notifications</h3>
          </div>

          {alerts.length === 0 ? (
            <div className="px-4 py-8 text-center text-gray-500 text-sm">
              No notifications
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {alerts.map(alert => {
                const style = TYPE_STYLES[alert.type] || TYPE_STYLES.info;
                const AlertIcon = style.icon;
                return (
                  <div
                    key={alert.id}
                    onClick={() => handleAlertClick(alert)}
                    className={`px-4 py-3 hover:bg-white/5 transition-colors ${
                      alert.actionUrl ? 'cursor-pointer' : ''
                    } ${!alert.read ? 'bg-brand-500/5' : ''}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`mt-0.5 p-1.5 rounded-lg ${style.bg} ${style.border} border`}>
                        <AlertIcon className={`${style.color}`} size={14} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-gray-800 dark:text-white truncate">
                            {alert.title}
                          </p>
                          <button
                            onClick={(e) => handleDismiss(alert.id, e)}
                            className="text-gray-500 hover:text-gray-300 ml-2 flex-shrink-0 transition-colors"
                          >
                            <FiX size={12} />
                          </button>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">
                          {alert.message}
                        </p>
                        {alert.actionLabel && (
                          <span className="inline-flex items-center gap-1 text-xs text-brand-400 font-medium mt-1.5 hover:text-brand-300 transition-colors">
                            {alert.actionLabel} <FiArrowRight size={10} />
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AlertBell;
